import React, { useEffect, useState } from 'react';
import { CHAPTER_STATE_EVENT, OPEN_CHAPTER_EVENT, TOGGLE_CHAPTER_EVENT } from './ChapterFold';
import { ChapterIcon } from './Illustrations';

// Desktop: a drawn signpost on the hill with one plank per chapter, a table of
// contents for the folded chapters below. Clicking a tile opens its chapter and scrolls to it;
// clicking an open chapter's tile closes it again. Hidden on phones, which
// use the chapter cards instead.

export interface ChapterInfo {
  id: string;
  number: string;
  meta: string;
  title: string;
  summary: string;
}

const ChapterIndex: React.FC<{ chapters: ChapterInfo[] }> = ({ chapters }) => {
  const [openIds, setOpenIds] = useState<string[]>([]);

  useEffect(() => {
    const onState = (event: Event) => {
      const { id, open } = (event as CustomEvent<{ id: string; open: boolean }>).detail ?? {};
      if (!id) return;
      setOpenIds((ids) => (open ? Array.from(new Set([...ids, id])) : ids.filter((x) => x !== id)));
    };
    window.addEventListener(CHAPTER_STATE_EVENT, onState);
    return () => window.removeEventListener(CHAPTER_STATE_EVENT, onState);
  }, []);

  const choose = (id: string) => {
    if (openIds.includes(id)) {
      window.dispatchEvent(new CustomEvent(TOGGLE_CHAPTER_EVENT, { detail: { id } }));
      return;
    }
    window.dispatchEvent(new CustomEvent(OPEN_CHAPTER_EVENT, { detail: { id } }));
    const el = document.getElementById(id);
    if (el) window.scrollTo({ top: Math.max(0, el.offsetTop - 64), behavior: 'smooth' });
  };

  return (
    <nav className="chapter-index" aria-labelledby="chapter-index-title">
      <div className="chapter-index-scene" aria-hidden="true">
        <svg className="ci-sun" viewBox="0 0 120 120"><circle cx="60" cy="60" r="54" /></svg>
        <svg className="ci-cloud" viewBox="0 0 120 50"><use href="#il-cloud" /></svg>
        <svg className="ci-hill" viewBox="0 0 1280 200" preserveAspectRatio="none"><path d="M0 70 Q320 -10 640 30 Q960 70 1280 10 V200 H0Z" /></svg>
        <svg className="ci-trees" viewBox="0 0 110 80"><use href="#il-tree" x="0" y="0" width="54" height="80" /><use href="#il-tree" x="60" y="20" width="40" height="60" /></svg>
      </div>
      <div className="chapter-index-inner">
        <header className="chapter-index-head">
          <p className="ed-eyebrow">Five chapters</p>
          <h2 id="chapter-index-title">The <em>guide</em></h2>
        </header>
        <span className="chapter-index-post" aria-hidden="true" />
        <ul>
          {chapters.map((c) => {
            const isOpen = openIds.includes(c.id);
            return (
              <li key={c.id}>
                <button
                  type="button"
                  className={isOpen ? 'is-open' : ''}
                  aria-expanded={isOpen}
                  aria-controls={c.id}
                  onClick={() => choose(c.id)}
                >
                  <span className="chapter-index-badge"><ChapterIcon id={c.id} /></span>
                  <span className="chapter-index-text">
                    <span className="chapter-index-row">
                      <span className="chapter-index-num" aria-hidden="true">{c.number}</span>
                      <small>{c.meta}</small>
                      {isOpen && <span className="chapter-index-hint">open, tap to close ↑</span>}
                    </span>
                    <strong>{c.title}</strong>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </nav>
  );
};

export default ChapterIndex;
